import { useMemo, useState } from "react";

import { JobsFilters } from "./JobsFilters";
import { JobsTable } from "./JobsTable";

import { Column, ErrorText, LoadingIndicator } from "@/components";
import { useGetJobsByCompanyId } from "@/hooks/queries/useGetJobsByCompanyId";
import { defaultJobsSortKeyAndOrder } from "@/lib/constants";
import { cn } from "@/lib/utils";
import { JobsResponse, JobsSortKeyAndOrder, JobStatus } from "@/types";

const filterAndSortJobs = ({
  cities,
  jobs,
  sortKeyAndOrder,
  statuses,
  title,
}: {
  cities: string[];
  jobs: JobsResponse[];
  sortKeyAndOrder: JobsSortKeyAndOrder;
  statuses: JobStatus[];
  title: string;
}) => {
  let result = [...jobs];

  // 1. Filter by city.
  const citySet = new Set(cities);
  result = result.filter(
    ({ job }) =>
      // If the city set is empty, then we are in a default
      // state and we want to show jobs across all cities.
      (citySet.size === 0 && job.city !== "") || citySet.has(job.city),
  );

  // 2. Filter by job status.
  const statusesSet = new Set(statuses);
  result = result.filter(
    ({ job }) =>
      // If the statuses set is empty, then we are in a default
      // state and we only want show jobs that are not closed.
      (statusesSet.size === 0 && job.status !== JobStatus.CLOSED) ||
      statusesSet.has(job.status),
  );

  // 3. Filter by job title.
  result = result.filter(({ job }) =>
    job.title.toLowerCase().includes(title.toLowerCase()),
  );

  // 4. Sort the jobs.
  result.sort(({ job: jobA }, { job: jobB }) => {
    switch (sortKeyAndOrder) {
      case JobsSortKeyAndOrder.CreatedAtAsc:
        return (
          new Date(jobA.createdAt).getTime() -
          new Date(jobB.createdAt).getTime()
        );
      case JobsSortKeyAndOrder.CreatedAtDesc:
        return (
          new Date(jobB.createdAt).getTime() -
          new Date(jobA.createdAt).getTime()
        );
      case JobsSortKeyAndOrder.TitleAsc:
        return jobA.title.localeCompare(jobB.title);
      case JobsSortKeyAndOrder.TitleDesc:
        return jobB.title.localeCompare(jobA.title);
      default:
        return 0;
    }
  });

  return result;
};

export const JobsFiltersAndTable = ({ className }: { className?: string }) => {
  /* Database CRUD Operations */
  // Read
  const { data: jobs = [], isPending, isError } = useGetJobsByCompanyId();

  /* React Hooks */
  // useState
  const [title, setTitle] = useState("");
  const [sortKeyAndOrder, setSortKeyAndOrder] = useState(
    defaultJobsSortKeyAndOrder,
  );
  const [cities, setCities] = useState<string[]>([]);
  const [statuses, setStatuses] = useState<JobStatus[]>([]);

  // useMemo
  const allCities = useMemo(
    () => Array.from(new Set(jobs.map(({ job }) => job.city))),
    [jobs],
  );

  const filteredAndSortedJobs = useMemo(
    () =>
      filterAndSortJobs({
        cities,
        jobs,
        sortKeyAndOrder,
        statuses,
        title,
      }),
    [cities, jobs, sortKeyAndOrder, statuses, title],
  );

  /* Render */
  const render = () => {
    if (isPending) {
      return <LoadingIndicator variant="medium" />;
    }
    if (isError) {
      return <ErrorText errorMessage="Failed to load jobs." />;
    }

    return (
      <Column className={cn("gap-4", className)}>
        <JobsFilters
          allCities={allCities}
          cities={cities}
          setCities={setCities}
          setSortOrderAndKey={setSortKeyAndOrder}
          setStatuses={setStatuses}
          setTitle={setTitle}
          sortKeyAndOrder={sortKeyAndOrder}
          statuses={statuses}
          title={title}
        />
        <JobsTable jobs={filteredAndSortedJobs} />
      </Column>
    );
  };

  return render();
};
